import { useEffect } from 'react';
import dayjs from 'dayjs';
import { useRouter } from 'next/router';
import { web2app } from '@kcd/app-interface';
import { trackCreativeView } from '@kcd/event-tracker';

import { BRIDGE_ROUTER } from '~/constants/router';
import { MARKET_HOME_REDIRECT_MMP_CODE } from '~/domains/mmp/constants';
import type { RedirectTemplate } from '~/domains/mmp/queries/usePlacementRaw';
import { usePlacementRaw } from '~/domains/mmp/queries/usePlacementRaw';
import { getCreativesFromPlacementRaw } from '~/domains/mmp/utils';
import {
  IS_BIG_SALE,
  PLUS_MEMBERSHIP_EVENT,
} from '~/domains/plusMembership/constants';
import { usePlusMembershipEvent } from '~/domains/plusMembership/queries';
import { useBusinessIdStore } from '~/store/global/useBusinessIdStore';
import { isBetweenDate } from '~/utils/date';

const MarketHomeRedirectCheck = () => {
  const router = useRouter();
  const { businessId } = useBusinessIdStore();
  const { data: placementData, isLoading: isPlacementLoading } =
    usePlacementRaw<RedirectTemplate>(
      businessId,
      MARKET_HOME_REDIRECT_MMP_CODE,
    );
  const { data: membershipEventData, isLoading: isMembershipEventLoading } =
    usePlusMembershipEvent();

  useEffect(() => {
    if (isPlacementLoading || isMembershipEventLoading) return;

    const today = dayjs();

    // 1. 1일 이내 접근 체크 (최우선)
    const lastRedirectDate = localStorage.getItem(PLUS_MEMBERSHIP_EVENT);
    if (lastRedirectDate) {
      const lastDate = dayjs(lastRedirectDate);
      const oneDayAfter = lastDate.add(1, 'day').startOf('day');

      // 마지막 리다이렉트로부터 1일이 지나지 않았으면 동작하지 않음
      if (today.isBefore(oneDayAfter)) return;
    }

    // 2. usePlacementRaw 체크
    const creatives = getCreativesFromPlacementRaw(placementData);
    const firstCreative = creatives[0];

    if (firstCreative) {
      const { endDate, redirectUrl } = firstCreative.template;

      if (endDate && redirectUrl) {
        const end = dayjs(endDate);

        trackCreativeView({
          adGroupId: firstCreative.ad_group_id,
          creativeId: firstCreative.creative_id,
          placementName: placementData?.placements[0].name || '',
        });

        // 오늘 날짜가 endDate 이전이거나 같으면 리다이렉트
        if (today.isBefore(end) || today.isSame(end, 'day')) {
          localStorage.setItem(PLUS_MEMBERSHIP_EVENT, today.toISOString());
          web2app.openModal({
            url: redirectUrl,
            fallback: () => window.open(redirectUrl, '_blank'),
          });
          return;
        }
      }
    }

    // 3. usePlusMembershipEvent 체크
    const isTargetUser =
      isBetweenDate(
        membershipEventData?.event?.startAt || '',
        membershipEventData?.event?.endAt || '',
      ) &&
      membershipEventData?.event?.remainDiscountAmount &&
      !IS_BIG_SALE;

    if (isTargetUser) {
      localStorage.setItem(PLUS_MEMBERSHIP_EVENT, today.toISOString());
      void router.push(BRIDGE_ROUTER.weeklyEvent);
    }

    // 4. 위 조건에 걸리지 않으면 아무것도 하지 않음
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placementData, membershipEventData, router]);

  return null;
};

export default MarketHomeRedirectCheck;
